import type {
  CreateMutationOptions,
  CreateQueryOptions,
} from '@tanstack/angular-query-experimental';
import { z } from 'zod';
import { isString } from '../../shared/helpers/is-string/is-string.helper';
import { LoggerService } from '../services/logger.service';
import { enqueueSnackbarConfigurationSchema } from './snackbar.models';

/**
 * Convenience type for TanStack Query options with sensible generic ordering.
 *
 * @template TQueryFunctionData - Raw data returned by the query function.
 * @template TQueryKey - Query key type.
 * @template TData - Selected/mapped data type (defaults to TQueryFnData).
 * @template TError - Error type (defaults to unknown).
 */
export type CreateQueryOptionsFor<
  TQueryFunctionData,
  TQueryKey extends readonly unknown[],
  TData = TQueryFunctionData,
  TError = unknown,
> = CreateQueryOptions<TQueryFunctionData, TError, TData, TQueryKey>;

/**
 * Represents a set of options for creating a mutation with customizable data types, variables,
 * mutation result handling, and error handling.
 *
 * This type is a generic extension of `CreateMutationOptions`, allowing flexibility for specifying
 * the types of the data, variables, mutation result, and potential error types relevant to the mutation logic.
 *
 * @template TData - Specifies the type of the returned data after the mutation is completed.
 * @template TVariables - Specifies the type of the variables required to execute the mutation.
 * @template TOnMutateResult - Defines the type of the result returned by the optional `onMutate`
 *                             callback function prior to the mutation's completion.
 * @template TError - Specifies the type of the potential error that can be returned in case of a failure.
 */
export type CreateMutationOptionsFor<
  TData,
  TVariables,
  TOnMutateResult = unknown,
  TError = unknown,
> = CreateMutationOptions<TData, TError, TVariables, TOnMutateResult>;

const unknownQuerySchema = z.object({ queryKey: z.unknown() });
const unknownMutationSchema = z.unknown();

const successSnackbarResolverPayloadSchema = z.object({
  variables: z.unknown(),
  data: z.unknown(),
});

export type SuccessSnackbarResolverPayload = z.infer<
  typeof successSnackbarResolverPayloadSchema
>;

const errorSnackbarResolverPayloadSchema = z.object({
  variables: z.unknown(),
  error: z.unknown(),
});

export type ErrorSnackbarResolverPayload = z.infer<
  typeof errorSnackbarResolverPayloadSchema
>;

const successSnackbarMetaSchema = z.union([
  enqueueSnackbarConfigurationSchema,
  z.function({
    input: [successSnackbarResolverPayloadSchema],
    output: enqueueSnackbarConfigurationSchema,
  }),
]);

const errorSnackbarMetaSchema = z.union([
  enqueueSnackbarConfigurationSchema,
  z.function({
    input: [errorSnackbarResolverPayloadSchema],
    output: enqueueSnackbarConfigurationSchema,
  }),
]);

const logErrorPayloadSchema = z.object({
  name: z.string().optional(),
  message: z.string().optional(),
  data: z.record(z.string(), z.string()).optional(),
});

type LogErrorPayload = z.infer<typeof logErrorPayloadSchema>;

const logErrorMetaSchema = z.union([
  z.literal(true),
  logErrorPayloadSchema,
  z.function({
    input: [
      z.object({
        error: z.unknown(),
        query: unknownQuerySchema.optional(),
        mutation: unknownMutationSchema.optional(),
        variables: z.unknown().optional(),
        context: z.unknown().optional(),
      }),
    ],
    output: logErrorPayloadSchema,
  }),
]);

type LogErrorMeta = z.infer<typeof logErrorMetaSchema>;

const tanStackMetaSchema = z.object({
  errorSnackbar: errorSnackbarMetaSchema.optional(),
  successSnackbar: successSnackbarMetaSchema.optional(),
  logError: logErrorMetaSchema.optional(),
});

export type TanStackMeta = z.infer<typeof tanStackMetaSchema>;

const _resolveSnackbarConfigurationPayloadSchema = z.discriminatedUnion(
  'kind',
  [
    errorSnackbarResolverPayloadSchema.extend({
      meta: z.unknown(),
      kind: z.literal('error'),
    }),
    successSnackbarResolverPayloadSchema.extend({
      meta: z.unknown(),
      kind: z.literal('success'),
    }),
  ],
);

export type ResolveSnackbarConfigurationPayload = z.infer<
  typeof _resolveSnackbarConfigurationPayloadSchema
>;

const logIfNeededExtraPayloadSchema = z.object({
  query: unknownQuerySchema.optional(),
  mutation: unknownMutationSchema.optional(),
  variables: z.unknown().optional(),
  context: z.unknown().optional(),
});

const _logIfNeededPayloadSchema = z.object({
  loggerService: z.instanceof(LoggerService),
  error: z.unknown(),
  meta: z.unknown(),
  extra: logIfNeededExtraPayloadSchema,
});

export type LogIfNeededPayload = z.infer<typeof _logIfNeededPayloadSchema>;

export const isTanStackMeta = (
  maybeTanStackMeta: unknown,
): maybeTanStackMeta is TanStackMeta =>
  tanStackMetaSchema.safeParse(maybeTanStackMeta).success;
/**
 * Extracts and returns the `logError` property from the given metadata object if it matches the expected type.
 *
 * @param {unknown} meta - The metadata object to evaluate.
 * @returns {LogErrorMeta | undefined} The `logError` property if the metadata matches the expected type; otherwise, undefined.
 */
export const getLogError = (meta: unknown): LogErrorMeta | undefined => {
  return isTanStackMeta(meta) ? meta.logError : undefined;
};

/**
 * Converts an unknown value into a standard Error object.
 *
 * This function ensures that the returned value is always an instance of Error
 * regardless of the input. If the input is already an instance of Error, it is
 * returned as-is. If the input is a string, it is wrapped in a new Error
 * object. For all other input types, a generic Error with the message
 * "Unknown error" is created and returned.
 *
 * @param {unknown} error - The value to normalize into an Error object.
 * @returns {Error} An instance of Error derived from the input.
 */
export const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (isString(error)) {
    return new Error(error);
  }

  return new Error('Unknown error');
};

/**
 * Resolves the log payload based on the provided log error metadata
 * and additional parameters. It processes log errors that could
 * be a boolean, a function, or an object, and returns a consistent
 * log error payload.
 *
 * @param {LogErrorMeta} logError - Metadata or behavior describing the log error.
 *                                   It could be a boolean, a function, or an object.
 * @param {Object} extra - Additional parameters to be used when the logError
 *                         is a function that requires these parameters for processing.
 * @returns {LogErrorPayload} The resolved log error payload.
 */
export const resolveLogPayload = (
  logError: LogErrorMeta,
  extra: Parameters<Exclude<LogErrorMeta, true | LogErrorPayload>>[0],
): LogErrorPayload => {
  if (logError === true) {
    return {};
  }
  if (typeof logError === 'function') {
    return logError(extra);
  }

  return logError;
};
